import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { createInquiry } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
    property_location: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const locations = ['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud', 'Other'];
  const reasons = [
    { value: 'looking', label: 'Looking for a Property' },
    { value: 'selling', label: 'Selling My Property' },
    { value: 'leasing', label: 'Leasing My Property' },
    { value: 'management', label: 'Property Management' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createInquiry({
        ...form,
        inquiry_type: form.reason || 'consultation',
      });
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setForm({ name: '', email: '', phone: '', reason: '', property_location: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-warm-sand py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-charcoal mb-6">
              Get in Touch
            </h1>
            <p className="font-body text-lg text-charcoal-light">
              Whether you're looking to list your property, find your dream villa, or simply learn more about our services, we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="font-heading text-2xl font-medium text-charcoal mb-2">
                Send Us a Message
              </h2>
              <p className="text-charcoal-light mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">Name *</label>
                    <Input
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="bg-white"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">Email *</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="bg-white"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">Phone</label>
                    <Input
                      type="tel"
                      placeholder="+62 812 3456 7890"
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-white"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">Reason *</label>
                    <Select
                      value={form.reason}
                      onValueChange={(v) => setForm(prev => ({ ...prev, reason: v }))}
                    >
                      <SelectTrigger className="bg-white" data-testid="contact-reason">
                        <SelectValue placeholder="How can we help?" />
                      </SelectTrigger>
                      <SelectContent>
                        {reasons.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Property Location</label>
                  <Select
                    value={form.property_location}
                    onValueChange={(v) => setForm(prev => ({ ...prev, property_location: v }))}
                  >
                    <SelectTrigger className="bg-white" data-testid="contact-location">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Message *</label>
                  <Textarea
                    placeholder="Tell us about your property or how we can help you..."
                    value={form.message}
                    onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                    required
                    rows={6}
                    className="bg-white resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full px-8 py-3"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Get a Free Consultation
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-warm-sand rounded-3xl p-8 lg:p-10 mb-8">
                <h3 className="font-heading text-xl font-medium text-charcoal mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-ubud-jungle" />
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-1">Office Address</h4>
                      <p className="text-charcoal-light text-sm">
                        Jalan Umalas Tunon<br />
                        Kerobokan Kelod, Kuta Utara<br />
                        Badung, Bali 80361
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-ubud-jungle" />
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-1">Phone</h4>
                      <a href="tel:+628778442377" className="text-charcoal-light text-sm hover:text-ubud-jungle">
                        +62 877 8442 3377
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-ubud-jungle" />
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-1">Email</h4>
                      <a href="mailto:hello@lalita.id" className="text-charcoal-light text-sm hover:text-ubud-jungle">
                        hello@lalita.id
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-ubud-jungle" />
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-1">Office Hours</h4>
                      <p className="text-charcoal-light text-sm">
                        Monday - Friday: 9:00 AM - 6:00 PM<br />
                        Saturday: 10:00 AM - 4:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="rounded-3xl overflow-hidden h-72 shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3944.2!2d115.1589!3d-8.6557!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sJl.%20Umalas%20Tunon%2C%20Kerobokan%20Kelod%2C%20Kuta%20Utara%2C%20Badung%2C%20Bali%2080361!5e0!3m2!1sen!2sid!4v1700000000000!5m2!1sen!2sid"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lalita Properties Office Location"
                  className="grayscale hover:grayscale-0 transition-all duration-300"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
